import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { ChipList } from "shared/ui/chip-list/ChipList"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { MediaOption } from "shared/ui/media-option/MediaOption"

import { OnRemoveReferenceFilter } from "./ReferenceFilterChipListView"
import {
  ReferenceBuildingTypeFilterItem,
  ReferenceFilterItem,
  ReferenceFilterType,
  ReferenceProductFilterItem,
  ReferenceRoomTypeFilterItem
} from "./types/ReferenceFilterType"

export interface ReferenceFilterChipProps {
  chip: ReferenceFilterItem
  onRemove: OnRemoveReferenceFilter
}

const ProductChipContent = React.memo(
  ({ chip }: { chip: ReferenceProductFilterItem }) => (
    <MediaOption.Root variant="product">
      {chip.productObj && <MediaOption.Media>{chip.image}</MediaOption.Media>}
      <MediaOption.Label>
        {chip.productObj?.get("productType")}
      </MediaOption.Label>
      <MediaOption.Subtext>{chip.productObj?.get("kind")}</MediaOption.Subtext>
    </MediaOption.Root>
  )
)
ProductChipContent.displayName = "ProductChipContent"

const BuildingTypeChipContent = React.memo(
  ({ chip }: { chip: ReferenceBuildingTypeFilterItem }) => {
    const { t } = useTranslation()
    return (
      <MediaOption.Root variant="stacked">
        {chip.icon && (
          <MediaOption.Media>
            <Icon name={chip.icon} />
          </MediaOption.Media>
        )}
        <MediaOption.Label>{chip.label}</MediaOption.Label>
        <MediaOption.Subtext>
          {t("BuildingReference.BuildingType.BuildingType")}
        </MediaOption.Subtext>
      </MediaOption.Root>
    )
  }
)
BuildingTypeChipContent.displayName = "BuildingTypeChipContent"

const RoomTypeChipContent = React.memo(
  ({ chip }: { chip: ReferenceRoomTypeFilterItem }) => {
    const { t } = useTranslation()
    return (
      <MediaOption.Root variant="stacked">
        <MediaOption.Label>{chip.label}</MediaOption.Label>
        <MediaOption.Subtext>{t("RoomType.RoomType")}</MediaOption.Subtext>
      </MediaOption.Root>
    )
  }
)
RoomTypeChipContent.displayName = "RoomTypeChipContent"

const ChipContent = React.memo(({ chip }: { chip: ReferenceFilterItem }) => {
  if (chip.filterType === ReferenceFilterType.PRODUCT)
    return <ProductChipContent chip={chip} />
  if (chip.filterType === ReferenceFilterType.BUILDING_TYPE)
    return <BuildingTypeChipContent chip={chip} />
  return <RoomTypeChipContent chip={chip} />
})
ChipContent.displayName = "ChipContent"

export const ReferenceFilterChip = React.memo(
  ({ chip, onRemove }: ReferenceFilterChipProps) => {
    const handleClick = useCallback(() => {
      onRemove(chip.filterType, chip.value)
    }, [chip.filterType, chip.value, onRemove])

    return (
      <ChipList.Chip as="button" onClick={handleClick}>
        <ChipContent chip={chip} />
        <Icon name={IconName.CLOSE} />
      </ChipList.Chip>
    )
  }
)
ReferenceFilterChip.displayName = "ReferenceFilterChip"
